import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { InstrumentDetailForm } from "../components/instruments/InstrumentDetailForm";
import { InstrumentSystemUserForm } from "../components/instruments/InstrumentSystemUserForm";
import { InstrumentTestsTable } from "../components/instruments/InstrumentTestsTable";
import { Section } from "../components/Section";
import { useTabSearch } from "../hooks";

export function EditInstrumentPage(params: { id: number }) {
  const tabsProps = useTabSearch();

  const { data, isPending, error } = trpc.admin.instrument.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const instrument = data;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/instruments" }}>
              Instruments
            </Breadcrumb>
            <Breadcrumb>
              {instrument.manufacturer} {instrument.model}
            </Breadcrumb>
          </>
        }
        actions={
          <DeleteInstrumentDialog instrument={instrument}>
            <Button variant="destructive">Delete</Button>
          </DeleteInstrumentDialog>
        }
      >
        {`${instrument.manufacturer} ${instrument.model}`}
      </Section.Title>
      <Tabs {...tabsProps} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
          <Tab id="tests">Test specs</Tab>
          <Tab id="system-user">System User</Tab>
        </TabsList>
        <TabPanel id="details">
          <InstrumentDetailForm instrument={instrument} />
        </TabPanel>
        <TabPanel id="tests">
          <InstrumentTestsTable instrument={instrument} />
        </TabPanel>
        <TabPanel id="system-user" className="flex-1 flex flex-col">
          <InstrumentSystemUserForm instrument={instrument} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeleteInstrumentDialog({
  children,
  instrument,
}: {
  children: ReactNode;
  instrument: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteInstrument = trpc.admin.instrument.delete.useMutation();

  const navigate = useNavigate();

  const onConfirm = () => {
    deleteInstrument.mutate(
      instrument,
      mutationResponse({
        errorTitle: "Error deleting instrument",
        successMessage: "Instrument deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/instruments" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete instrument"
      message="Are you sure you want to delete this instrument?"
      onConfirm={onConfirm}
      loading={deleteInstrument.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
